import "./App.css";
import { useMemo, useState, useEffect, React } from "react";
import AppContext from "./AppContext";
import { ThemeProvider } from "styled-components";
import { theme } from "utilities/utils";
import { supabase } from "supabaseClient";
import BlogPost from "website/homeblog";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { BudgetStateProvider } from "hooks/useBudgetState";
import { SidebarProvider } from 'contexts/SidebarContext';
import { ChallengesStateProvider } from 'hooks/useChallengesState';
import { TeamProvider } from 'contexts/TeamContext';
import { CalendarStateProvider } from 'hooks/useCalendarState';

const BottomBar = styled.div`
  width: 100%;
  height: 75px;
  background: black; 
  position: fixed;
`;

export default function App({ children }) {
  // Auth Data
  const [platformUrl, setPlatformUrl] = useState(null);
  const [platformUser, setPlatformUser] = useState(null);
  const [session, setSession] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Access Permissions
  // ## Registered an Account
  // ## Has a Profile
  // ## Is approved by ADMIN

  const [isRegistered, setIsRegistered] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [isApproved, setIsApproved] = useState(false);

  // User Data
  const [userRole, setUserRole] = useState(null);
  const [userOrganization, setUserOrganization] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Content Data
  const [platformData, setPlatformData] = useState(null);
  const [eventsData, setEventsData] = useState([]);

  // Navigation

  const [selectedNavItem, setSelectedNavItem] = useState([]);
  const [platformNav, setPlatformNav] = useState("Dashboard");

  // Blog
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogPages, setBlogPages] = useState([]);
  const [selectedPost, setSelectedPost] = useState();
  const [selectedLink, setSelectedLink] = useState();

  // Presentation
  const [isMobile, setIsMobile] = useState(false);

  // Set Url
  const baseurl = process.env.REACT_APP_SERVER_URL;
  const fullurl = `${baseurl}`;
  useEffect(() => {
    setPlatformUrl(fullurl);
  }, [fullurl, platformUrl]);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Get initial session
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(initialSession);
        setPlatformUser(initialSession?.user || null);
      } catch (error) {
        console.error('Error initializing auth:', error);
        setAuthError(error.message);
      }
    };
    
    initAuth();
  }, []);

  // Auth state change listener
  useEffect(() => {
    const {
      data: { subscription: authSubscription }
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log('Auth event:', event);
      setSession(currentSession);
      setPlatformUser(currentSession?.user || null);

      // Handle token refresh
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
      }

      // Handle signed out
      if (event === 'SIGNED_OUT') {
        setPlatformUser(null);
        setSession(null);
        setUserProfile(null);
        setHasProfile(false);
        setIsApproved(false);
      }
    });

    // Cleanup subscription
    return () => {
      authSubscription?.unsubscribe();
    };
  }, []);

  // Profile fetching
  useEffect(() => {
    const fetchProfile = async () => {
      if (!platformUser?.id) return;

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select()
          .eq("id", platformUser.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setHasProfile(false);
          setUserProfile(null);
          return;
        }

        if (data) {
          setHasProfile(true);
          setUserProfile(data.profiledata);
        } else {
          setHasProfile(false);
          setUserProfile(null);
        }
      } catch (err) {
        console.error('Error in profile fetch:', err);
        setHasProfile(false);
        setUserProfile(null);
      }
    };

    fetchProfile();
  }, [platformUser?.id]);

  // Session refresh timer
  useEffect(() => {
    if (!session) return;

    const refreshToken = async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) throw error;
        console.log('Session refreshed via timer');
      } catch (error) {
        console.error('Error refreshing session:', error);
      }
    };

    // Refresh 5 minutes before expiry
    const timeUntilExpiry = session.expires_at * 1000 - Date.now();
    const refreshTime = Math.max(0, timeUntilExpiry - (5 * 60 * 1000));
    
    const refreshTimer = setTimeout(refreshToken, refreshTime);
    
    return () => clearTimeout(refreshTimer);
  }, [session]);

  const contextValue = useMemo(
    () => ({
      platformUser,
      setPlatformUser,
      isRegistered,
      setIsRegistered,
      hasProfile,
      setHasProfile,
      isApproved,
      setIsApproved,
      platformUrl,
      setPlatformUrl,
      platformData,
      setPlatformData,
      platformNav,
      setPlatformNav,
      eventsData,
      setEventsData,
      isMobile,
      setIsMobile,
      selectedNavItem,
      setSelectedNavItem,
      selectedPost,
      setSelectedPost,
      selectedLink,
      setSelectedLink,
      blogPosts,
      setBlogPosts,
      blogPages,
      setBlogPages,
      userRole,
      setUserRole,
      userOrganization,
      setUserOrganization,
      userProfile,
      setUserProfile,
      session,
      authError
    }),
    [
      selectedNavItem,
      platformUrl,
      platformUser,
      isRegistered,
      hasProfile,
      isApproved,
      platformData,
      platformNav,
      eventsData,
      isMobile,
      selectedPost,
      selectedLink,
      blogPosts,
      blogPages,
      userRole,
      userOrganization,
      userProfile,
      session,
      authError
    ]
  );

  return (
    <SidebarProvider>
      <ThemeProvider theme={theme}>
        <AppContext.Provider value={contextValue}>
          <TeamProvider>
            <ChallengesStateProvider>
              <BudgetStateProvider>
                <CalendarStateProvider>
                  <div className="App">{children}</div>
                </CalendarStateProvider>
              </BudgetStateProvider>
            </ChallengesStateProvider>
          </TeamProvider>
        </AppContext.Provider>
      </ThemeProvider>
    </SidebarProvider>
  );
}
